import { useMemo } from 'react';
import { useTenant } from '@/hooks/useTenant';

/**
 * Feature entitlement by pricing band.
 *
 * Until the billing tables exist the source is companies.subscription_tier:
 *   null / free / starter → Band A
 *   growth                → Band B
 *   enterprise (pro)      → Band C
 * A NULL tier means "no plan assigned yet" (legacy + comped tenants) and is
 * treated as entitled, matching public.company_has_entitlement() in SQL.
 * When plans ship, swap the source here and in that function only.
 */
export type Band = 'A' | 'B' | 'C';
export type Feature = 'google_ads_conversions' | 'xero_sync';

const FEATURE_MIN_BAND: Record<Feature, Band> = {
  google_ads_conversions: 'B',
  xero_sync: 'B',
};

const BAND_RANK: Record<Band, number> = { A: 0, B: 1, C: 2 };

export function bandForTier(tier: string | null | undefined): Band | null {
  const t = (tier ?? '').trim().toLowerCase();
  if (!t) return null;
  if (t === 'free' || t === 'starter') return 'A';
  if (t === 'growth') return 'B';
  return 'C';
}

export function useEntitlements() {
  const { company, isLoadingCompany } = useTenant();
  const tier = (company as { subscription_tier?: string | null } | null)?.subscription_tier ?? null;

  return useMemo(() => {
    const band = bandForTier(tier);
    const can = (feature: Feature): boolean => {
      if (band === null) return true; // no plan yet → unrestricted
      return BAND_RANK[band] >= BAND_RANK[FEATURE_MIN_BAND[feature]];
    };
    return { band, tier, can, isLoading: isLoadingCompany };
  }, [tier, isLoadingCompany]);
}
